"use client";
import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Icons
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>;
const FileIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /></svg>;
const LoaderIcon = () => <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>;

export default function SalaryHistoryPage() {
    const [employees, setEmployees] = useState<any[]>([]);
    const [periods, setPeriods] = useState<any[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [designations, setDesignations] = useState<any[]>([]);

    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [fromPeriodId, setFromPeriodId] = useState('');
    const [toPeriodId, setToPeriodId] = useState('');

    // Filters for employee selection
    const [empFilters, setEmpFilters] = useState({
        companyId: '',
        departmentId: '',
        designationId: '',
        search: ''
    });

    const [historyData, setHistoryData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [empRes, perRes, compRes, deptRes, desigRes] = await Promise.all([
                    fetch('/api/employees'),
                    fetch('/api/payroll-periods'),
                    fetch('/api/companies'),
                    fetch('/api/departments'),
                    fetch('/api/designations')
                ]);

                setEmployees(await empRes.json());
                const periodsData = await perRes.json();
                setPeriods(periodsData);
                setCompanies(await compRes.json());
                setDepartments(await deptRes.json());
                setDesignations(await desigRes.json());

                if (periodsData.length > 0) {
                    // Set default range: last 6 months or all if less
                    setFromPeriodId(periodsData[Math.min(periodsData.length - 1, 5)].id.toString());
                    setToPeriodId(periodsData[0].id.toString());
                }
            } catch (error) {
                console.error("Failed to load initial data", error);
            } finally {
                setInitialLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const fetchHistory = async () => {
        if (!selectedEmployeeId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('employeeId', selectedEmployeeId);
            if (fromPeriodId) params.append('startPeriodId', fromPeriodId);
            if (toPeriodId) params.append('endPeriodId', toPeriodId);

            const res = await fetch(`/api/payroll/history?${params.toString()}`);
            const data = await res.json();
            setHistoryData(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to fetch history", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedEmployeeId) fetchHistory();
        else setHistoryData([]);
    }, [selectedEmployeeId, fromPeriodId, toPeriodId]);

    const filteredEmployees = employees.filter(emp => {
        if (empFilters.companyId && emp.company_id?.toString() !== empFilters.companyId) return false;
        if (empFilters.departmentId && emp.department_id?.toString() !== empFilters.departmentId) return false;
        if (empFilters.designationId && emp.designation_id?.toString() !== empFilters.designationId) return false;
        if (empFilters.search) {
            const s = empFilters.search.toLowerCase();
            return emp.name.toLowerCase().includes(s) || emp.employee_id.toLowerCase().includes(s);
        }
        return true;
    });

    const formatCurrency = (val: any) => {
        const num = parseFloat(val) || 0;
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);
    };

    const numberToWords = (num: number) => {
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const format = (n: number): string => {
            if (n < 20) return a[n];
            const digit = n % 10;
            if (digit === 0) return b[Math.floor(n / 10)];
            return b[Math.floor(n / 10)] + ' ' + a[digit];
        };

        const convert = (n: number): string => {
            if (n === 0) return 'Zero';
            let res = '';
            if (n >= 10000000) { res += format(Math.floor(n / 10000000)) + 'Crore '; n %= 10000000; }
            if (n >= 100000) { res += format(Math.floor(n / 100000)) + 'Lakh '; n %= 100000; }
            if (n >= 1000) { res += format(Math.floor(n / 1000)) + 'Thousand '; n %= 1000; }
            if (n >= 100) { res += format(Math.floor(n / 100)) + 'Hundred '; n %= 100; }
            if (n > 0) {
                if (res !== '') res += 'and ';
                res += format(n);
            }
            return res.trim();
        };

        const total = Math.floor(num);
        const paise = Math.round((num - total) * 100);
        let result = convert(total) + ' Rupees';
        if (paise > 0) result += ' and ' + convert(paise) + ' Paise';
        return result + ' Only';
    };

    const generatePDF = async () => {
        if (historyData.length === 0) return;
        setGenerating(true);
        try {
            const doc = new jsPDF('l', 'mm', 'a2'); // Use A2 landscape to fit all requested columns comfortably
            const emp = historyData[0];
            const formatCurrencyShort = (val: any) => {
                const num = parseFloat(val);
                if (isNaN(num)) return '0.00';
                return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            };

            // Set global settings
            doc.setTextColor(0, 0, 0);

            // 1. Logo and Header
            try {
                doc.addImage('/logo.png', 'PNG', 20, 15, 25, 25);
            } catch (e) {
                console.warn("Logo not found at /logo.png");
            }

            // Main Header - Company Full Name (Centered correctly for A2: 594/2 = 297)
            doc.setFontSize(30);
            doc.setFont("helvetica", "bold");
            const companyName = emp.company_full_name || emp.company || "Manikanta Group";
            doc.text(companyName.toUpperCase(), 297, 30, { align: 'center' });

            // Sub-Header - Salary History Report
            doc.setFontSize(20);
            doc.setFont("helvetica", "bold");
            doc.text("SALARY HISTORY REPORT", 297, 45, { align: 'center' });

            // 2. Employee Info Section
            autoTable(doc, {
                startY: 55,
                theme: 'grid',
                styles: { fontSize: 13, textColor: [0, 0, 0], cellPadding: 4, lineWidth: 0.25, lineColor: [0, 0, 0] },
                body: [
                    [
                        { content: 'Employee Name', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
                        { content: emp.employee_name, styles: { fontStyle: 'bold' } },
                        { content: 'Employee ID', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
                        { content: emp.employee_id, styles: { fontStyle: 'bold' } }
                    ],
                    [
                        { content: 'Department', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
                        { content: emp.department },
                        { content: 'Designation', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
                        { content: emp.designation }
                    ],
                    [
                        { content: 'Company', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
                        { content: emp.company },
                        { content: 'Period Covered', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
                        {
                            content: `${periods.find(p => p.id.toString() === fromPeriodId)?.month} ${periods.find(p => p.id.toString() === fromPeriodId)?.year} to ${periods.find(p => p.id.toString() === toPeriodId)?.month} ${periods.find(p => p.id.toString() === toPeriodId)?.year}`,
                            styles: { fontStyle: 'bold' }
                        }
                    ]
                ],
            });

            // 3. Main Data Table
            autoTable(doc, {
                startY: (doc as any).lastAutoTable.finalY + 10,
                theme: 'grid',
                head: [
                    [
                        { content: 'MONTH', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                        { content: 'ATTENDANCE', colSpan: 5, styles: { halign: 'center', fillColor: [230, 230, 230] } },
                        { content: 'SALARY EARNINGS', colSpan: 10, styles: { halign: 'center', fillColor: [230, 230, 230] } },
                        { content: 'SALARY DEDUCTIONS', colSpan: 7, styles: { halign: 'center', fillColor: [230, 230, 230] } },
                        { content: 'NET PAYABLE', colSpan: 3, styles: { halign: 'center', fillColor: [230, 230, 230] } },
                        { content: 'REMARKS', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }
                    ],
                    [
                        'TD', 'PD', 'WH', 'OT/Arr', 'SD',
                        'Salary', 'Actual Basic', 'Earned Basic', 'HRA', 'Conv', 'Med', 'Spl All', 'Earned Sal', 'OT/Arr Amt', 'Gross Sal',
                        'EPF', 'PT', 'ESI', 'LWF', 'Adv', 'Cant', 'Total Ded',
                        'Net Sal', 'Ret (5%)', 'Net Pay'
                    ]
                ],
                body: historyData.map(row => [
                    row.period,
                    // Attendance
                    row.total_days, row.paid_days, row.weekly_holidays, row.ot_arrears_days, row.salary_days,
                    // Earnings
                    formatCurrencyShort(row.salary), formatCurrencyShort(row.actual_basic), formatCurrencyShort(row.earned_basic),
                    formatCurrencyShort(row.earned_hra), formatCurrencyShort(row.earned_conveyance), formatCurrencyShort(row.earned_medical),
                    formatCurrencyShort(row.earned_special_allowance),
                    formatCurrencyShort(Number(row.earned_basic) + Number(row.earned_hra) + Number(row.earned_conveyance) + Number(row.earned_medical) + Number(row.earned_special_allowance)),
                    formatCurrencyShort(row.ot_arrears_amount), formatCurrencyShort(row.gross_salary),
                    // Deductions
                    formatCurrencyShort(row.epf), formatCurrencyShort(row.pt), formatCurrencyShort(row.esi),
                    formatCurrencyShort(row.lwf), formatCurrencyShort(row.advance), formatCurrencyShort(row.canteen), formatCurrencyShort(row.total_deductions),
                    // Net
                    formatCurrencyShort(row.net_salary), formatCurrencyShort(row.retention_amount), formatCurrencyShort(row.net_pay),
                    row.remarks || ''
                ]),
                styles: { fontSize: 12.1, textColor: [0, 0, 0], cellPadding: 2, lineWidth: 0.2, lineColor: [0, 0, 0] },
                headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.25, lineColor: [0, 0, 0], halign: 'center' },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 35 },
                    5: { fontStyle: 'bold' }, // SD
                    13: { fontStyle: 'bold' }, // Earned Sal
                    15: { fontStyle: 'bold' }, // Gross Sal
                    22: { fontStyle: 'bold' }, // Total Ded
                    25: { fontStyle: 'bold', fillColor: [240, 240, 240] } // Net Pay
                },
                didParseCell: function (data) {
                    if (data.section === 'body' && data.column.index >= 1 && data.column.index <= 5) {
                        data.cell.styles.halign = 'center';
                    }
                    if (data.section === 'body' && data.column.index >= 6 && data.column.index <= 25) {
                        data.cell.styles.halign = 'right';
                    }
                }
            });

            doc.save(`${emp.employee_id}_Salary_History.pdf`);
        } catch (error) {
            console.error("PDF Error:", error);
            alert("Failed to generate PDF");
        } finally {
            setGenerating(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="flex h-[80vh] items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 max-w-full mx-auto min-h-screen pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Salary History</h1>
                    <p className="text-slate-500 mt-1">Detailed month-wise earnings and deductions report</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={generatePDF}
                        disabled={historyData.length === 0 || generating}
                        className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                    >
                        {generating ? <LoaderIcon /> : <DownloadIcon />}
                        Generate PDF Report
                    </button>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

                {/* Filters Sidebar */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                        <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <SearchIcon /> Selection Filters
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Company</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition text-sm"
                                    value={empFilters.companyId}
                                    onChange={e => setEmpFilters(f => ({ ...f, companyId: e.target.value }))}
                                >
                                    <option value="">All Companies</option>
                                    {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Department</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition text-sm"
                                    value={empFilters.departmentId}
                                    onChange={e => setEmpFilters(f => ({ ...f, departmentId: e.target.value }))}
                                >
                                    <option value="">All Departments</option>
                                    {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Select Employee</label>
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        placeholder="Search by name or ID..."
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition text-sm mb-2"
                                        value={empFilters.search}
                                        onChange={e => setEmpFilters(f => ({ ...f, search: e.target.value }))}
                                    />
                                    <select
                                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition text-sm h-48"
                                        size={10}
                                        value={selectedEmployeeId}
                                        onChange={e => setSelectedEmployeeId(e.target.value)}
                                    >
                                        <option value="" disabled>--- Select an Employee ---</option>
                                        {filteredEmployees.map(emp => (
                                            <option key={emp.employee_id} value={emp.employee_id}>
                                                {emp.name} ({emp.employee_id})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                        <h2 className="text-sm font-bold text-slate-800 mb-4 uppercase tracking-widest">Time range</h2>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1 block">From</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition text-sm"
                                    value={fromPeriodId}
                                    onChange={e => setFromPeriodId(e.target.value)}
                                >
                                    {periods.map(p => <option key={p.id} value={p.id}>{p.month} {p.year}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1 block">To</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition text-sm"
                                    value={toPeriodId}
                                    onChange={e => setToPeriodId(e.target.value)}
                                >
                                    {periods.map(p => <option key={p.id} value={p.id}>{p.month} {p.year}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Table Area */}
                <div className="lg:col-span-3">
                    <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden min-h-[500px]">
                        {loading ? (
                            <div className="flex h-[500px] items-center justify-center">
                                <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : !selectedEmployeeId ? (
                            <div className="flex flex-col h-[500px] items-center justify-center text-slate-400">
                                <FileIcon />
                                <p className="mt-4 font-bold uppercase tracking-widest text-sm">Select an employee to view history</p>
                            </div>
                        ) : historyData.length === 0 ? (
                            <div className="flex flex-col h-[500px] items-center justify-center text-slate-400 text-center px-10">
                                <div className="text-4xl">🤷‍♂️</div>
                                <p className="mt-4 font-bold uppercase tracking-widest text-sm">No records found for the selected period</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse min-w-[2000px]">
                                    <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold tracking-widest sticky top-0 z-20 shadow-sm">
                                        <tr>
                                            <th className="p-4 bg-slate-50 sticky left-0 z-30">Month</th>
                                            <th className="p-4 text-center border-l bg-slate-100" colSpan={5}>Attendance</th>
                                            <th className="p-4 text-center border-l bg-blue-50/30" colSpan={9}>Salary Earnings</th>
                                            <th className="p-4 text-center border-l bg-red-50/30" colSpan={7}>Salary Deductions</th>
                                            <th className="p-4 text-center border-l bg-green-50/30" colSpan={3}>Summary</th>
                                            <th className="p-4 border-l">Remarks</th>
                                        </tr>
                                        <tr className="border-b bg-white">
                                            <th className="p-3 sticky left-0 bg-white z-30"></th>
                                            {/* Attendance */}
                                            <th className="p-3 text-center border-l min-w-[50px]">TD</th>
                                            <th className="p-3 text-center min-w-[50px]">PD</th>
                                            <th className="p-3 text-center min-w-[50px]">WH</th>
                                            <th className="p-3 text-center min-w-[50px]">OT/Arr</th>
                                            <th className="p-3 text-center min-w-[50px] font-bold text-indigo-600">SD</th>
                                            {/* Earnings */}
                                            <th className="p-3 text-center border-l min-w-[90px]">Salary</th>
                                            <th className="p-3 text-center min-w-[90px]">Actual B.</th>
                                            <th className="p-3 text-center min-w-[90px]">Earned B.</th>
                                            <th className="p-3 text-center min-w-[80px]">HRA</th>
                                            <th className="p-3 text-center min-w-[80px]">Conv.</th>
                                            <th className="p-3 text-center min-w-[80px]">Med.</th>
                                            <th className="p-3 text-center min-w-[80px]">Spl All.</th>
                                            <th className="p-3 text-center min-w-[100px] font-bold text-blue-600">Earned Sal</th>
                                            <th className="p-3 text-center min-w-[100px]">OT Arr Amt</th>
                                            {/* Deductions */}
                                            <th className="p-3 text-center border-l min-w-[80px]">EPF</th>
                                            <th className="p-3 text-center min-w-[70px]">PT</th>
                                            <th className="p-3 text-center min-w-[80px]">ESI</th>
                                            <th className="p-3 text-center min-w-[70px]">LWF</th>
                                            <th className="p-3 text-center min-w-[80px]">Adv.</th>
                                            <th className="p-3 text-center min-w-[80px]">Cant.</th>
                                            <th className="p-3 text-center min-w-[100px] font-bold text-red-600">Total Ded</th>
                                            {/* Summary */}
                                            <th className="p-3 text-center border-l min-w-[100px]">Net Sal</th>
                                            <th className="p-3 text-center min-w-[90px] text-orange-600">Ret (5%)</th>
                                            <th className="p-3 text-center min-w-[110px] font-extrabold text-green-600">Net Pay</th>
                                            <th className="p-3 border-l min-w-[150px]"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {historyData.map((row) => {
                                            const earnedSalary = Number(row.earned_basic) + Number(row.earned_hra) + Number(row.earned_conveyance) + Number(row.earned_medical) + Number(row.earned_special_allowance);
                                            return (
                                                <tr key={row.period_id} className="hover:bg-slate-50 transition text-sm">
                                                    <td className="p-4 font-bold text-slate-700 bg-white sticky left-0 z-10 shadow-[2px_0_5px_rgba(0,0,0,0.05)] border-r">
                                                        {row.period_month} {row.period_year}
                                                    </td>

                                                    {/* Attendance */}
                                                    <td className="p-3 text-center">{row.total_days || '-'}</td>
                                                    <td className="p-3 text-center">{row.paid_days || '-'}</td>
                                                    <td className="p-3 text-center">{row.weekly_holidays || '-'}</td>
                                                    <td className="p-3 text-center">{row.ot_arrears_days || '-'}</td>
                                                    <td className="p-3 text-center font-bold text-indigo-600">{row.salary_days || '-'}</td>

                                                    {/* Earnings */}
                                                    <td className="p-3 text-center border-l">{formatCurrency(row.salary)}</td>
                                                    <td className="p-3 text-center text-slate-500">{formatCurrency(row.actual_basic)}</td>
                                                    <td className="p-3 text-center">{formatCurrency(row.earned_basic)}</td>
                                                    <td className="p-3 text-center">{formatCurrency(row.earned_hra)}</td>
                                                    <td className="p-3 text-center">{formatCurrency(row.earned_conveyance)}</td>
                                                    <td className="p-3 text-center">{formatCurrency(row.earned_medical)}</td>
                                                    <td className="p-3 text-center">{formatCurrency(row.earned_special_allowance)}</td>
                                                    <td className="p-3 text-center font-bold text-blue-600">{formatCurrency(earnedSalary)}</td>
                                                    <td className="p-3 text-center">{formatCurrency(row.ot_arrears_amount)}</td>

                                                    {/* Deductions */}
                                                    <td className="p-3 text-center border-l text-red-500">{formatCurrency(row.epf)}</td>
                                                    <td className="p-3 text-center text-red-500">{formatCurrency(row.pt)}</td>
                                                    <td className="p-3 text-center text-red-500">{formatCurrency(row.esi)}</td>
                                                    <td className="p-3 text-center text-red-500">{formatCurrency(row.lwf)}</td>
                                                    <td className="p-3 text-center text-red-500">{formatCurrency(row.advance)}</td>
                                                    <td className="p-3 text-center text-red-500">{formatCurrency(row.canteen)}</td>
                                                    <td className="p-3 text-center font-bold text-red-600">{formatCurrency(row.total_deductions)}</td>

                                                    {/* Summary */}
                                                    <td className="p-3 text-center border-l font-medium">{formatCurrency(row.net_salary)}</td>
                                                    <td className="p-3 text-center text-orange-600">{formatCurrency(row.retention_amount)}</td>
                                                    <td className="p-3 text-center font-extrabold text-green-600 bg-green-50/50">{formatCurrency(row.net_pay)}</td>

                                                    <td className="p-3 border-l text-xs text-slate-500">{row.remarks}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
